import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'
import ReactList from 'react-list'
import moment from 'moment'
import { Link } from 'react-router-dom'
import some from 'lodash/some'

import { TableHeader } from './TableHeader'
import { Button } from '../../../components/buttons'
import { ppsAction } from '../../../actions'
import { setLobProgress } from '../../../function/project-progress'
import { ppsSelector } from '../../../selectors'
import { toNumber } from '../../../function/math'
import { Item } from './item'
import { FONTS } from '../../../constants'
import GraphIcon from '../../../static/images/graph-icon.svg'

export const PercentTable = React.memo(forwardRef(({
  month,
  projectID,
  isEditing,
  profitData,
  handleDisplayPercent,
}, ref) => {
  const access_token = localStorage.getItem('access_token')
  const dispatch = useDispatch()
  const [crRevenue, setCrRevenue] = useState([])
  const [projectProgresses, setProjectProgresses] = useState({})
  const [progresses, setProgresses] = useState([])
  const isFetching = useSelector(ppsSelector.isFetching)
  const percentTable = useSelector(ppsSelector.percentTable)
  const _progresses = useSelector(ppsSelector.progresses)
  const canSaveProgress = useSelector(ppsSelector.canSaveProgress)

  useImperativeHandle(ref, () => ({
    getProgressesData() {
      const data = []
      progresses.map(p => {
        p.lobs.map(lob => {
          data.push({
            allocation_id: p.allocation_id,
            lob_id: lob.lob_id,
            remark: lob._remark,
            man_day_rate: lob._man_day_rate,
            activity_base: {
              accumulate_percentage: lob._activity_base.accumulate_percentage,
              accumulate_revenue: lob._activity_base.accumulate_revenue,
              delta_percentage: lob._activity_base.delta_percentage,
              delta_revenue: lob._activity_base.delta_revenue,
            },
          })
          return lob
        })
        return p
      })
      return { id: projectProgresses._id, progresses: data }
    },
    fetchProgressesData() {
      fetchData()
    },
  }))

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    if (_progresses._project_progresses) {
      const {
        is_submit_pps,
        project_progress_snapshot = [],
        _project_progresses = [],
      } = _progresses

      if (is_submit_pps) {
        setProgresses(project_progress_snapshot)
        setCrRevenue(project_progress_snapshot.filter(r => r.type === 'CR'))
      } else {
        setProgresses(_project_progresses)
        setCrRevenue(_project_progresses.filter(r => r.type === 'CR'))
      }
      setProjectProgresses(_progresses)
      dispatch(ppsAction.resetProjectProgresses())
    } else if (_progresses.type === 'error') {
      setProgresses(null)
    }
  }, [_progresses])

  const fetchData = () => {
    dispatch(ppsAction.fetchProjectProgress(projectID, moment(month).startOf('month').format('YYYY-MM-DD'), access_token))
  }

  const handleExpandTable = id => {
    let expand = { ...percentTable.expand }
    if (expand[id]) {
      delete expand[id]
    } else {
      expand = { ...expand, [id]: true }
    }
    dispatch(ppsAction.setPercentTable({ ...percentTable, expand }))
  }

  const handleInput = (e, progressIndex, index, name) => {
    const _progresses = [...progresses]
    const progress = _progresses[progressIndex]
    const lobs = [...progress.lobs]
    const lob = lobs[index]
    setLobProgress(lob, name, e)
    if (name === 'man_day_rate') {
      lob._man_day_rate = e
    }
    lobs[index] = lob
    progress.lobs = lobs
    _progresses[progressIndex] = progress
    setProgresses(_progresses)

    if (some(_progresses, p => {
      return p.type !== 'SUBSCRIPTION' && some(p.lobs, l => {
        return l.criteria !== 'MD' && toNumber(l._activity_base.accumulate_percentage) > 100
      })
    })) {
      dispatch(ppsAction.setCanSaveProgress(false))
    } else if (!canSaveProgress) {
      dispatch(ppsAction.setCanSaveProgress(true))
    }
  }

  const handleRemark = (e, progressIndex, index) => {
    const _progresses = [...progresses]
    const progress = _progresses[progressIndex]
    const lobs = [...progress.lobs]
    const lob = lobs[index]
    lob._remark = e
    progress.lobs = lobs
    _progresses[progressIndex] = progress
    setProgresses(_progresses)
  }

  const renderItem = (index) => {
    const item = progresses[index]
    const isExpand = percentTable.expand[index]
    return (
      <Item
        key={ index }
        progressIndex={ index }
        crRevenue={ crRevenue }
        data={ item }
        isEditing={ isEditing }
        isExpand={ isExpand }
        profitData={ profitData }
        handleExpandTable={ handleExpandTable }
        handleInput={ handleInput }
        handleRemark={ handleRemark }
      />
    )
  }

  const length = progresses ? progresses.length : 0

  return (
    <Root>
      <TableHeader
        profitData={ profitData }
        handleDisplayPercent={ handleDisplayPercent }
      />
      { !isFetching && !progresses ?
        <TextWrapper>
          <Icon src={ GraphIcon } />
          <br />
          <BoldText>No Data</BoldText>
          <Text>No Data of Update Percent was found</Text>
        </TextWrapper>
        :
        (
          !isFetching && progresses && progresses.length === 0 ?
            <TextWrapper>
              <Icon src={ GraphIcon } />
              <br />
              <BoldText>No revenue planning found</BoldText>
              <Text>Please update your revenue planning in project information to proceed</Text>
              <Link
                to={ {
                  pathname: `/project_information/${projectID}`,
                  state: { header: 'Project Information', activeTab: '3' },
                } }
              >
                <Button
                  theme='#28a745'
                  width='165px'
                  height='26px'
                  style={ { marginTop: 8 } }
                >
                  Go to Revenue Planning
                </Button>
              </Link>
            </TextWrapper>
            :
            <Wrapper>
              <ReactList
                itemRenderer={ renderItem }
                length={ length }
              />
            </Wrapper>
        )
      }
    </Root>
  )
}))

const Root = styled.div`
  display: flex;
  flex-direction: column;
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  margin-top: 10%;
`

const Icon = styled.img`
  height: 20%;
  width: 20%;
`

const Text = styled.span`
  font-family: ${FONTS.GOTHAM};
  font-size: ${FONTS.SMALL_SIZE};
`

const BoldText = styled.span`
  font-family: ${FONTS.GOTHAM_MEDIUM};
  font-size: ${FONTS.TITLE_SIZE};
`



// WEBPACK FOOTER //
// ./src/components/project-profit-sheet/PercentTable/index.js